import { jobsAPI } from '../lib/api';

export const JOB_POLL_INTERVAL_MS = 3000;
export const JOB_POLL_TIMEOUT_MS = 6 * 60 * 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export class JobError extends Error {
  constructor(code, detail) {
    super(code);
    this.code = code;
    this.detail = detail;
  }
}

/**
 * Poll a background job until it is done. Resolves with the job result,
 * rejects with JobError('job_failed' | 'job_timeout'). `onStatus` receives
 * every observed status so UIs can display real progress.
 */
export const waitForJob = async (jobId, { onStatus, intervalMs = JOB_POLL_INTERVAL_MS, timeoutMs = JOB_POLL_TIMEOUT_MS } = {}) => {
  const startedAt = Date.now();
  let transientErrors = 0;
  while (Date.now() - startedAt < timeoutMs) {
    await sleep(intervalMs);
    let data;
    try {
      const res = await jobsAPI.status(jobId);
      data = res.data;
      transientErrors = 0;
    } catch (err) {
      // tolerate a few network hiccups while polling
      transientErrors += 1;
      if (transientErrors >= 4 || err.response?.status === 404) throw new JobError('job_failed', err.message);
      continue;
    }
    if (onStatus) onStatus(data.status);
    if (data.status === 'done') return data.result || {};
    if (data.status === 'failed') throw new JobError('job_failed', data.error);
  }
  throw new JobError('job_timeout');
};

/** Start a job via `startFn` (returns axios response with {job_id}) and wait for it. */
export const runJob = async (startFn, options) => {
  const start = await startFn();
  return waitForJob(start.data.job_id, options);
};
